import { useStore } from 'app/context';
import { UserModel } from 'app/models/core/user.model';
import { BnrActionCountRow } from 'app/stores/admin.log.store';
import { isRelevantUser } from './UsageAnmeldequoteShared';
import { UsageCompareRows, UsageSlide, UsageSource } from './UsageReportShared';
import { formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useState } from 'react';

interface VdViewRow {
	vd: string;
	activeCount: number;
	viewCount: number;
	prevViewCount: number;
	avg: number;
}

const buildRows = (users: UserModel[], currCounts: BnrActionCountRow[], prevCounts: BnrActionCountRow[]): VdViewRow[] => {
	const currByBnr = new Map(currCounts.map((c) => [c.bnr_id, c.action_count]));
	const prevByBnr = new Map(prevCounts.map((c) => [c.bnr_id, c.action_count]));

	const byVd = new Map<string, VdViewRow>();
	users.forEach((u) => {
		if (!isRelevantUser(u)) {
			return;
		}
		const vd = u.firstVd;
		if (!vd) {
			return;
		}
		let row = byVd.get(vd);
		if (!row) {
			row = { vd, activeCount: 0, viewCount: 0, prevViewCount: 0, avg: 0 };
			byVd.set(vd, row);
		}
		row.activeCount++;
		row.viewCount += currByBnr.get(u.bnrId) || 0;
		row.prevViewCount += prevByBnr.get(u.bnrId) || 0;
	});

	const rows = Array.from(byVd.values());
	rows.forEach((r) => {
		r.avg = r.activeCount > 0 ? Math.round((r.viewCount / r.activeCount) * 10) / 10 : 0;
	});
	return rows;
};

// Delta in % zum Vorjahr; '–' solange es keine Vorjahresdaten gibt
const deltaChip = (curr: number, prev: number): { text: string; dir: 'up' | 'down' | 'flat' } => {
	if (prev <= 0) {
		return { text: '–', dir: 'flat' };
	}
	const pct = Math.round(((curr - prev) / prev) * 100);
	return { text: `${pct >= 0 ? '+' : ''}${pct}%`, dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
};

// Folien "Aufrufe nach VD", "Aufrufe nach VD im Vgl. zum Vorjahr" und
// "Aufrufe je Anwender nach VD". Aufrufe = Seitenaufrufe (Navigations-Tracking),
// Vorjahreswert über den gleichen Zeitraum (1.1. bis heute), damit YTD vergleichbar ist.
// Achtung: Navigation wird erst seit dem 19.3.2026 getrackt.
export const UsageViewsCharts = observer(() => {
	const { adminLogStore, adminUserStore } = useStore();
	const [rows, setRows] = useState<VdViewRow[]>([]);
	const [loading, setLoading] = useState(true);

	const currYear = moment().year();
	const prevYear = currYear - 1;

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			const [users, currCounts, prevCounts] = await Promise.all([
				adminUserStore.findAllActiveUsers(),
				adminLogStore.loadActionCountsByBnrId('nav', `${currYear}-01-01`, moment().add(1, 'day').format('YYYY-MM-DD')),
				adminLogStore.loadActionCountsByBnrId('nav', `${prevYear}-01-01`, moment().subtract(1, 'year').add(1, 'day').format('YYYY-MM-DD')),
			]);
			setRows(buildRows(users, currCounts, prevCounts));
			setLoading(false);
		};
		load();
	}, [adminLogStore, adminUserStore, currYear, prevYear]);

	if (loading) {
		return <div>Wird geladen...</div>;
	}

	const totalActive = rows.reduce((sum, r) => sum + r.activeCount, 0);
	const totalViews = rows.reduce((sum, r) => sum + r.viewCount, 0);
	const totalPrevViews = rows.reduce((sum, r) => sum + r.prevViewCount, 0);
	const totalAvg = totalActive > 0 ? Math.round((totalViews / totalActive) * 10) / 10 : 0;

	// Folie: Aufrufe nach VD (absteigend sortiert)
	const byViews = [...rows].sort((a, b) => b.viewCount - a.viewCount);
	const maxViews = Math.max(1, ...byViews.map((r) => Math.max(r.viewCount, r.prevViewCount)));

	// Folie: Aufrufe je Anwender nach VD, Ø ABV als Referenzlinie
	const byAvg = [...rows].sort((a, b) => b.avg - a.avg);
	const maxAvg = Math.max(1, totalAvg, ...byAvg.map((r) => r.avg));

	return (
		<>
			<UsageSlide title={`Aufrufe nach VD ${currYear}`} source={UsageSource.dvs}>
				<UsageCompareRows
					rows={byViews.map((r) => ({ label: r.vd, curr: r.viewCount }))}
					max={maxViews}
					formatValue={formatNum}
					summary={{ label: 'Σ ABV:', value: formatNum(totalViews) }}
				/>
			</UsageSlide>

			{/* ohne Vorjahresdaten ist die Folie wertlos */}
			{totalPrevViews > 0 && (
				<UsageSlide title={`Aufrufe nach VD im Vgl. zum Vorjahr`} source={UsageSource.dvs}>
					<UsageCompareRows
						rows={byViews.map((r) => ({
							label: r.vd,
							curr: r.viewCount,
							prev: r.prevViewCount,
							delta: deltaChip(r.viewCount, r.prevViewCount),
						}))}
						currLabel={`1-${moment().month() + 1} ${currYear}`}
						prevLabel={`1-${moment().month() + 1} ${prevYear}`}
						max={maxViews}
						formatValue={formatNum}
						summary={{ label: 'Σ ABV:', value: `${deltaChip(totalViews, totalPrevViews).text} (${formatNum(totalPrevViews)} → ${formatNum(totalViews)})` }}
					/>
				</UsageSlide>
			)}

			<UsageSlide title={`Aufrufe je Anwender nach VD ${currYear}`} source={UsageSource.dvs}>
				<UsageCompareRows
					rows={byAvg.map((r) => ({ label: r.vd, curr: r.avg }))}
					max={maxAvg}
					formatValue={formatNum}
					avg={{ value: totalAvg, label: `Ø ABV ${formatNum(totalAvg)}` }}
					summary={{ label: 'Ø ABV:', value: formatNum(totalAvg) }}
				/>
			</UsageSlide>
		</>
	);
});
